import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import {
  Box,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import React from "react";
import { RfItemAttachmentStatus } from "../../../../clients/graphqlTypes.js";
import {
  useRfItemAttachmentModifyMutation,
  useRfItemAttachmentsResolveDocumentTypesQuery,
} from "./RfQuoteItemAttachmentsBox.generated.js";
import { FilesBox } from "../../../commons/files/FilesBox.js";
import { ToggleClientVisibilityButton } from "../../../commons/files/ToggleClientVisibilityButton.js";

interface FileBoxItem {
  id: string;
  title: string;
  url: string;
  mimeType: string;
  docType?: string | null;
  status?: RfItemAttachmentStatus;
  clientVisibility?: boolean;
}

interface Props {
  projectId: string | null;
  docId: string;
  itemId: string;
  title?: string;
  attachments: Array<FileBoxItem> | undefined;
  isInitiallyClosed?: boolean;
  showImagesInGallery?: boolean;
  readOnly?: boolean;
  loading?: boolean;
  handleAdd: (files: File[]) => Promise<any> | void;
  handleDelete: (id: string) => Promise<any> | void;
  onDocTypeChange?: () => void;
  setClientVisibility?: (
    id: string,
    visibility: boolean
  ) => Promise<void> | void;
  renderAddButton?: (button: React.ReactNode) => React.ReactNode;
  renderDeleteButton?: (button: React.ReactNode) => React.ReactNode;
  renderDocTypeDropdown?: (dropdown: React.ReactNode) => React.ReactNode;
}

export const RfItemFileBox = ({
  projectId,
  docId,
  itemId,
  attachments = [],
  onDocTypeChange,
  setClientVisibility,
  renderAddButton,
  renderDeleteButton,
  renderDocTypeDropdown: renderDocTypeDropdownWrapper,
  ...filesBoxProps
}: Props) => {
  const client = useApolloClient();
  const [modifyAttachment] = useRfItemAttachmentModifyMutation({ client });

  const handleDocTypeChange = async (
    attachmentId: string,
    fileName: string,
    newDocType: string
  ) => {
    if (!projectId) return;

    try {
      await modifyAttachment({
        variables: {
          input: {
            projectId,
            docId,
            itemId,
            attachmentId,
            docType: newDocType,
          },
        },
      });
      onDocTypeChange?.();
    } catch (error) {
      console.error("Failed to update document type:", error);
    }
  };

  const renderDocTypeDropdown = (file: FileBoxItem) => {
    const dropdown = (
      <Box className="sap-doc-type-dropdown">
        <DocTypeDropdown
          projectId={projectId}
          docId={docId}
          fileName={file.title}
          currentDocType={file.docType || ""}
          onDocTypeChange={(newDocType: string) =>
            handleDocTypeChange(file.id, file.title, newDocType)
          }
          disabled={
            filesBoxProps.readOnly ||
            filesBoxProps.loading ||
            file.status !== "PENDING"
          }
        />
      </Box>
    );

    return renderDocTypeDropdownWrapper
      ? renderDocTypeDropdownWrapper(dropdown)
      : dropdown;
  };

  const renderVisibilityButton = (file: FileBoxItem) => (
    <ToggleClientVisibilityButton
      isVisible={file.clientVisibility ?? true}
      changeVisibility={isVisible => setClientVisibility?.(file.id, isVisible)}
      disabled={!setClientVisibility || filesBoxProps.loading || false}
    />
  );

  const customViewModes = [
    {
      viewMode: null,
      renderItemActionButton: renderDocTypeDropdown,
    },
    ...(setClientVisibility
      ? [
          {
            viewMode: "visibility" as const,
            renderItemActionButton: renderVisibilityButton,
          },
        ]
      : []),
  ];

  return (
    <FilesBox
      {...filesBoxProps}
      attachments={attachments}
      renderDeleteButton={renderDeleteButton}
      renderAddButton={renderAddButton}
      customViewModes={customViewModes}
    />
  );
};

interface DocTypeDropdownProps {
  projectId: string | null;
  docId: string;
  fileName: string;
  currentDocType: string;
  onDocTypeChange: (docType: string) => void;
  disabled?: boolean;
}

const DocTypeDropdown = ({
  projectId,
  docId,
  fileName,
  currentDocType,
  onDocTypeChange,
  disabled,
}: DocTypeDropdownProps) => {
  const client = useApolloClient();

  const { data, loading } = useRfItemAttachmentsResolveDocumentTypesQuery({
    client,
    variables: { projectId: projectId!, docId, fileName },
    skip: !projectId,
  });

  const documentTypes =
    getDataOrNull(data?.quoteSapS4HanaData)?.xAvailableDocumentTypes || [];

  const handleChange = (event: SelectChangeEvent<string>) => {
    onDocTypeChange(event.target.value);
  };

  return (
    <FormControl size="small" sx={{ width: 140 }}>
      <Select
        value={currentDocType}
        onChange={handleChange}
        disabled={disabled || loading}
        displayEmpty
        onClick={e => e.stopPropagation()}
        renderValue={selected => selected || "Select type"}
        sx={{
          height: 32,
          "& .MuiSelect-select": {
            paddingTop: "4px",
            paddingBottom: "4px",
          },
        }}
      >
        <MenuItem value="" disabled>
          Select type
        </MenuItem>
        {documentTypes.map(docType => (
          <MenuItem key={docType.value} value={docType.value}>
            {docType.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
